import React, { FC, useMemo } from 'react';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import {
	contractorLocationsRestCrudThunksForBuyer,
	contractorLocationsRestCrudThunksForSupplier,
} from '../../thunks/contractor_locations_thunks';
import { Button, Tag, Typography } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { CONTRACTOR_LOCATIONS_CRUD_ACTION_CREATORS } from '../../actions/contractor_locations_actions';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import { SelfManagedTag } from '../contractor_clients/ContractorClientsIndexPage';
import { contractorClientsRestCrudThunksForSupplier } from '../../thunks/contractor_clients_thunks';

const { Text } = Typography;
require('../locations_index_page/LocationsIndexPage.less');

const ContractorLocationsList: FC<any> = ({
	updateFilters,
	contractorLocations,
	clearAndUpdateFilters,
	history,
	location,
	userType,
	isMasterList,
	targetCollectionName = 'contractorLocationsIndex',
	contractorClientId,
	contractorClients,
	fetchContractorClients,
	fetchMultipleContractorClients,
}): React.ReactElement => {
	const columns = useMemo(
		() => [
			{
				title: 'Name',
				render: (_, record) => (
					<div className="locationsIndexPage__row">
						<div>
							<div className="flex">
								<div>
									<div className="locationsIndexPage__rowTitle ">
										<Ellipsis tooltip={true} length={64}>
											{record.name}
										</Ellipsis>
									</div>
									<div className="locationsIndexPage__rowSubtitle">
										<div className=" mb-2 flex flex-row items-baseline gap-4">
											<span>{nullSafeGet('primaryAddress.city', record)}</span>
											{nullSafeGet('storeId', record) ? (
												<span className="locationsIndexPage__rowInlineGroup2">{`Store ID : ${nullSafeGet(
													'storeId',
													record
												)}`}</span>
											) : null}
										</div>
										<div className="locationsIndexPage__rowInlineGroup">
											{nullSafeGet('contractorTenant.connectedLocation', record) ? (
												<span className="rowGrayChip">
													<i className="icons8-font icons8-location-marker" />
													{nullSafeGet('contractorTenant.connectedLocation.name', record)}
												</span>
											) : (
												SelfManagedTag
											)}
										</div>
										{record.isActive ? null : (
											<div className="locationsIndexPage__rowInlineGroup">
												<Text className={'mr-1'} type={'danger'}>
													Inactive
												</Text>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				),
			},
		],
		[]
	);

	const onRow = (record) => ({
		onClick: () =>
			history.push(
				getLinkWIthBackLinkParams(
					location,
					isMasterList ? 'Back to locations' : 'Back to Client',
					`/${userType}/contractor_clients/locations/detail/${record.id}/details`
				)
			),
	});

	const onAddLocation = () =>
		history.push(`/${userType}/contractor_clients/detail/${contractorClientId}/locations/new`);

	const filterConfig = useMemo(
		() => [
			{
				fieldName: 'isSelfManaged',
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Self Managed',
						value: 'true',
					},
					{
						label: 'Connected',
						value: 'false',
					},
				],
				filtersValueAccessor: (filtersStateSlice) =>
					nullSafeGet('isSelfManaged', filtersStateSlice),
				defaultValue: undefined,
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (isSelfManaged) => {
					updateFilters(
						{ isSelfManaged: isSelfManaged === 'undefined' ? undefined : isSelfManaged },
						targetCollectionName
					);
				},
				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				showOutsideDropdown: true,
			},
			...(isMasterList
				? [
						{
							label: 'Client',
							fieldName: 'contractorClientId',
							stateSlice: contractorClients,
							targetCollectionName: 'locationsAdvancedFiltersIndes',
							fetch: fetchContractorClients,
							fetchMultiple: fetchMultipleContractorClients,
						},
				  ]
				: []),
		],
		[
			contractorClients,
			fetchContractorClients,
			fetchMultipleContractorClients,
			isMasterList,
			targetCollectionName,
			updateFilters,
		]
	);

	return (
		<div className="locationsIndexPage">
			<PaginatedReduxTableWithHeader
				targetCollectionName={targetCollectionName}
				stateSlice={contractorLocations}
				updateFilters={updateFilters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				filterConfig={filterConfig}
				entityCollectionName="contractor_locations"
				tableColumns={columns}
				onTableRow={onRow}
				initialPagination={{ current: 1, pageSize: 10 }}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? contractorLocationsRestCrudThunksForSupplier.read
						: contractorLocationsRestCrudThunksForBuyer.read
				}
				handleQueryParams
				preAppliedFilters={{ ...(contractorClientId && { contractorClientId }) }}
				rightActions={
					isMasterList ? null : (
						<Button size="large" type="primary" onClick={onAddLocation}>
							Add Location
						</Button>
					)
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
								alt={`There are no locations for you!`}
							/>
						}
						headline={`There are no locations for you!`}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 560, marginBottom: 16 }}>
									Please add a new location to get started.
								</div>
							</div>
						}
					/>
				}
			/>
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	contractorLocations: state.contractor_locations,
	contractorClients: state.contractor_clients,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(CONTRACTOR_LOCATIONS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			CONTRACTOR_LOCATIONS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)
		),
	fetchMultipleContractorClients: (ids, targetCollectionName) =>
		dispatch(contractorClientsRestCrudThunksForSupplier.read({ ids }, targetCollectionName)),
	fetchContractorClients: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			contractorClientsRestCrudThunksForSupplier.read(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

const ComponentWithoutUserType = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ContractorLocationsList)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
